import React from "react";
import {Button, ButtonContainer, ButtonType, Modal} from "@renta-apps/athenaeum-react-components";
import {BaseComponent, IBaseComponent} from "@renta-apps/athenaeum-react-common";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import {FileModel, Utility} from "@renta-apps/athenaeum-toolkit";
import {UploadFullImageResponse} from "@/models/UploadFullImageResponse";
import FileService from "@/services/FileService";
import Localizer from "@/localization/Localizer";

import styles from "@/components/MassProducts/EditCheckItemPicturesModal/EditCheckItemPicturesModal.module.scss";
import ToolsImageInput from "@/components/ToolsImageInput/ToolsImageInput";
import UnleashHelper from "@/helpers/UnleashHelper";

export interface IEditCheckItemPicturesModalProps {
    onSubmit(sender: IBaseComponent, rowId: number, pictures: FileModel[] | null): Promise<void>;
}

export interface IEditCheckItemPicturesModalState {
    pictures: FileModel[] | null;
    initialHashCode: number;
}

export default class EditCheckItemPicturesModal extends BaseComponent<IEditCheckItemPicturesModalProps, IEditCheckItemPicturesModalState> {

    state: IEditCheckItemPicturesModalState = {
        pictures: null,
        initialHashCode: 0,
    }

    private readonly _modalRef: React.RefObject<Modal> = React.createRef();
    private _rowId: number = 0;

    private get modal(): Modal {
        return this._modalRef.current!;
    }

    private get isModified(): boolean {
        const hashCode: number = Utility.getHashCode(this.state.pictures);
        return hashCode != this.state.initialHashCode;
    }

    public async onChangeImageAsync(pictures: FileModel[]): Promise<void> {
        await this.setState({pictures});
    }

    private async onImageUploadAsync(file: FileModel): Promise<FileModel | null> {
        const response: UploadFullImageResponse | null = await FileService.uploadFullImageAsync(file);
        if (response?.fileModel) {
            return response.fileModel;
        }

        return null;
    }

    public async openAsync(rowId: number, pictures: FileModel[] | null): Promise<void> {
        this._rowId = rowId;
        const initialHashCode = Utility.getHashCode(pictures);

        await this.setState({initialHashCode, pictures});

        await this.modal.openAsync();
    }

    public async closeAsync(): Promise<void> {
        await this.clearStateAsync();
        await this.modal.closeAsync();
    }

    private async onSubmitAsync(): Promise<void> {
        await this.props.onSubmit(this, this._rowId, this.state.pictures);

        await this.onCloseAsync();
    }

    private async clearStateAsync(): Promise<void> {
        this.setState({pictures: null});
    }

    private async onCloseAsync(): Promise<void> {
        await this.clearStateAsync();
        await this.closeAsync();
    }

    public static get modalId(): string {
        return "editCheckItemPicturesModal";
    }

    render(): React.ReactNode {
        return (
            <Modal id={EditCheckItemPicturesModal.modalId}
                   ref={this._modalRef}
                   className={styles.editCheckItemPicturesModal}
                   title={Localizer.editCheckItemPicturesModalTitle}
                   onClose={() => this.onCloseAsync()}
            >

                <div className={styles.picture}>
                    <ToolsImageInput multi noAutoSelect
                                     noEdit={UnleashHelper.isEnabled(RentaToolsConstants.featureFlagDirectImageDownloads)}
                                     pictures={this.state.pictures}
                                     className={this.css("flex-1")}
                                     onChange={(pictures: FileModel[]) => this.onChangeImageAsync(pictures)}
                                     maxImageRequestSizeInBytes={RentaToolsConstants.originalImageMaxSize}
                                     convertImage={(image: FileModel) => this.onImageUploadAsync(image)}
                                     fileTypes={[...RentaToolsConstants.supportedImageFileTypes]}
                                     imageUrl={(image: FileModel) => image.src}
                    />
                </div>

                <ButtonContainer className={"mt-4"}>
                    <Button label={Localizer.genericCancel}
                            type={ButtonType.Light}
                            onClick={() => this.closeAsync()}
                    />

                    <Button label={Localizer.devicesFiltersModalButtonApply}
                            disabled={!this.isModified}
                            type={ButtonType.Orange}
                            onClick={() => this.onSubmitAsync()}
                    />
                </ButtonContainer>

            </Modal>
        )
    }
}